// The conflict check as an explicit verb with a stated verdict. Reused on
// the identity, opposing-parties and review steps; only the review step
// hands in onAcknowledge, because only /create can waive a critical.

import type { ConflictCandidate } from "../api/intake";
import { aNumberLabel } from "../../../lib/caseLabels";
import { SepList } from "../../../components/chrome/SepList";

const SEVERITY_COLOR: Record<ConflictCandidate["severity"], string> = {
  critical: "var(--red-700)",
  warning: "var(--warn-600)",
  info: "var(--muted)",
};

function ConflictRow({ c }: { c: ConflictCandidate }) {
  const color = SEVERITY_COLOR[c.severity] ?? "var(--muted)";
  const who = c.existingClient;
  return (
    <div className="intake-conflict-row" style={{ borderLeft: `3px solid ${color}` }}>
      <div className="intake-conflict-head">
        <span className="intake-conflict-pill" style={{ color, borderColor: color }}>
          {c.severity}
        </span>
        <span className="intake-conflict-type">{c.type}</span>
      </div>
      <div className="intake-conflict-msg">{c.message}</div>
      {who && (
        <div className="intake-conflict-who">
          <SepList
            parts={[
              `${who.firstName} ${who.lastName}`.trim(),
              who.alienNumber ? (
                <span className="tnum-mono">{aNumberLabel(who.alienNumber)}</span>
              ) : null,
              c.existingMatter?.caseNumber ? (
                <span className="tnum-mono">{c.existingMatter.caseNumber}</span>
              ) : null,
            ].filter(Boolean)}
            sep=" / "
          />
        </div>
      )}
      {c.details && <div className="intake-conflict-details">{c.details}</div>}
    </div>
  );
}

interface Props {
  /** null = the check has not run yet. */
  candidates: ConflictCandidate[] | null;
  checking: boolean;
  error: string | null;
  onRun?: () => void;
  runLabel?: string;
  /** The check needs its inputs first - shown disabled, never hidden. */
  disabled?: boolean;
  /** Present only where the waiver can actually be sent (the review step). */
  acknowledged?: boolean;
  onAcknowledge?: (v: boolean) => void;
}

export function ConflictSection({
  candidates,
  checking,
  error,
  onRun,
  runLabel = "Check for conflicts",
  disabled,
  acknowledged,
  onAcknowledge,
}: Props) {
  const criticals = (candidates ?? []).filter((c) => c.severity === "critical").length;
  return (
    <div className="intake-conflicts">
      <div className="intake-conflicts-bar">
        {onRun && (
          <button
            type="button"
            className="cc-link-btn"
            disabled={checking || disabled}
            onClick={onRun}
          >
            {checking ? "Checking..." : runLabel}
          </button>
        )}
        {candidates !== null && !checking && (
          <span
            role="status"
            className="intake-conflicts-verdict"
            style={{ color: criticals > 0 ? "var(--red-700)" : "var(--ok-600)" }}
          >
            {candidates.length === 0
              ? "No conflicts detected."
              : `${candidates.length} candidate${candidates.length === 1 ? "" : "s"} - review below.`}
          </span>
        )}
      </div>
      {error && (
        <div role="alert" className="cc-form-error">
          {error}
        </div>
      )}
      {(candidates ?? []).map((c, i) => (
        <ConflictRow key={`${c.type}-${c.existingClient?._id ?? i}`} c={c} />
      ))}
      {criticals > 0 && onAcknowledge && (
        <label className="cc-form-check intake-ack">
          <input
            type="checkbox"
            checked={acknowledged ?? false}
            onChange={(e) => onAcknowledge(e.target.checked)}
          />
          I have reviewed the critical conflict(s) above and confirm intake may proceed.
        </label>
      )}
    </div>
  );
}
